import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { accessesAPI } from '@/shared/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { Spinner } from '@travellegacy/nexops-design-system';
import { History, Search, TrendingUp, Users } from 'lucide-react';
import { formatDate } from '@/shared/lib/utils';
import type { UserAccess, AccessStats, UserRole } from '@/shared/types';
import { Input } from '@travellegacy/nexops-design-system';
import { Badge } from '@travellegacy/nexops-design-system';
import { Pagination } from '@travellegacy/nexops-design-system';
import { TabFilter } from '@travellegacy/nexops-design-system';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  SortableTable,
} from '@travellegacy/nexops-design-system';

export default function Accesses() {
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [roleFilter, setRoleFilter] = useState<UserRole | 'all'>('all');
  const [page, setPage] = useState(1);
  const limit = 50;

  const { data: accessesData, isLoading: isLoadingAccesses } = useQuery({
    queryKey: ['accesses', searchQuery, startDate, endDate, page],
    queryFn: async () => {
      const params: {
        search?: string;
        startDate?: string;
        endDate?: string;
        limit?: number;
        offset?: number;
      } = {
        limit,
        offset: (page - 1) * limit,
      };

      if (searchQuery) params.search = searchQuery;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await accessesAPI.list(params);
      return response.data as {
        data: UserAccess[];
        pagination: {
          total: number;
          page: number;
          pageSize: number;
          totalPages: number;
          hasNext: boolean;
          hasPrev: boolean;
        };
      };
    },
  });

  const { data: statsData, isLoading: isLoadingStats } = useQuery({
    queryKey: ['accesses-stats', startDate, endDate],
    queryFn: async () => {
      const params: {
        startDate?: string;
        endDate?: string;
      } = {};

      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await accessesAPI.stats(params);
      return response.data.data as AccessStats;
    },
  });

  // Calculate accesses by day for simple visualization
  const accessesByDayChart = useMemo(() => {
    if (!statsData?.accessesByDay) return [];
    return statsData.accessesByDay.slice(-30); // Last 30 days
  }, [statsData]);

  // Calculate role counts for tabs
  const roleCounts = useMemo(() => {
    if (!accessesData?.data) return { all: 0, ADMIN: 0, GESTOR: 0, COLLABORATOR: 0, CLIENT: 0 };

    const accesses = accessesData.data;
    return {
      all: accesses.length,
      ADMIN: accesses.filter((a) => a.user?.role === 'ADMIN').length,
      GESTOR: accesses.filter((a) => a.user?.role === 'GESTOR').length,
      COLLABORATOR: accesses.filter((a) => a.user?.role === 'COLLABORATOR').length,
      CLIENT: accesses.filter((a) => a.user?.role === 'CLIENT').length,
    };
  }, [accessesData]);

  // Filter accesses by role
  const filteredAccesses = useMemo(() => {
    if (!accessesData?.data) return [];
    if (roleFilter === 'all') return accessesData.data;
    return accessesData.data.filter((access) => access.user?.role === roleFilter);
  }, [accessesData, roleFilter]);

  // Tabs configuration
  const roleTabs = [
    { value: 'all', label: 'Todos', count: roleCounts.all, color: 'primary' as const },
    { value: 'ADMIN', label: 'Administradores', count: roleCounts.ADMIN, color: 'red' as const },
    { value: 'GESTOR', label: 'Gestores', count: roleCounts.GESTOR, color: 'blue' as const },
    { value: 'COLLABORATOR', label: 'Colaboradores', count: roleCounts.COLLABORATOR, color: 'gray' as const },
    { value: 'CLIENT', label: 'Clientes', count: roleCounts.CLIENT, color: 'green' as const },
  ];

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(1); // Reset to first page on search
  };

  const handleRoleFilterChange = (value: UserRole | 'all') => {
    setRoleFilter(value);
    setPage(1); // Reset to first page on filter change
  };

  const handleDateChange = (type: 'start' | 'end', value: string) => {
    if (type === 'start') {
      setStartDate(value);
    } else {
      setEndDate(value);
    }
    setPage(1); // Reset to first page on filter change
  };

  const isLoading = isLoadingAccesses || isLoadingStats;

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <div>
            <div className="h-9 w-32 bg-muted animate-pulse rounded" />
            <div className="h-5 w-96 bg-muted animate-pulse rounded mt-2" />
          </div>
        </div>

        {/* Stats Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-card rounded-lg border p-6 animate-pulse">
              <div className="h-5 w-32 bg-muted rounded mb-4" />
              <div className="h-8 w-24 bg-muted rounded mb-2" />
              <div className="h-4 w-full bg-muted rounded" />
            </div>
          ))}
        </div>

        {/* Filters Skeleton */}
        <div className="bg-card rounded-lg border p-6 animate-pulse">
          <div className="h-6 w-24 bg-muted rounded mb-4" />
          <div className="grid gap-4 md:grid-cols-3">
            <div className="h-10 bg-muted rounded" />
            <div className="h-10 bg-muted rounded" />
            <div className="h-10 bg-muted rounded" />
          </div>
        </div>

        {/* Chart Skeleton */}
        <div className="bg-card rounded-lg border p-6 animate-pulse">
          <div className="h-6 w-48 bg-muted rounded mb-4" />
          <div className="h-64 bg-muted rounded" />
        </div>

        {/* Table Skeleton */}
        <div className="bg-card rounded-lg border animate-pulse">
          <div className="p-6">
            <div className="h-6 w-40 bg-muted rounded mb-4" />
            <div className="space-y-3">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="h-16 bg-muted rounded" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Acessos</h1>
          <p className="text-muted-foreground mt-2">Histórico completo de acessos ao sistema</p>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Acessos</CardTitle>
            <History className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoadingStats ? '...' : statsData?.totalAccesses || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {startDate || endDate ? 'No período selecionado' : 'Últimos 180 dias'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Usuários Únicos</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoadingStats ? '...' : statsData?.uniqueUsers || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {startDate || endDate ? 'No período selecionado' : 'Últimos 180 dias'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Média por Dia</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {isLoadingStats
                ? '...'
                : statsData?.accessesByDay && statsData.accessesByDay.length > 0
                  ? Math.round(statsData.totalAccesses / statsData.accessesByDay.length).toFixed(0)
                  : 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {startDate || endDate ? 'No período selecionado' : 'Últimos 180 dias'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Accesses by Day Chart */}
      {accessesByDayChart.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Acessos por Dia (Últimos 30 dias)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={accessesByDayChart}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return date.toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                    });
                  }}
                  className="text-xs"
                  stroke="hsl(var(--muted-foreground))"
                />
                <YAxis
                  className="text-xs"
                  stroke="hsl(var(--muted-foreground))"
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '6px',
                  }}
                  labelFormatter={(value) => {
                    const date = new Date(value as string);
                    return date.toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    });
                  }}
                  formatter={(value: number) => [value, 'Acessos']}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={{ fill: 'hsl(var(--primary))', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 mb-4">
            <History className="h-5 w-5" />
            <span>Histórico de Acessos</span>
          </CardTitle>
          {/* Role Tabs */}
          <TabFilter
            tabs={roleTabs}
            currentTab={roleFilter}
            onChange={(value) => handleRoleFilterChange(value as UserRole | 'all')}
          />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Search and Date Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Buscar por nome ou email..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div>
                <Input
                  type="date"
                  placeholder="Data inicial"
                  value={startDate}
                  onChange={(e) => handleDateChange('start', e.target.value)}
                />
              </div>
              <div>
                <Input
                  type="date"
                  placeholder="Data final"
                  value={endDate}
                  onChange={(e) => handleDateChange('end', e.target.value)}
                />
              </div>
            </div>

            {/* Accesses Table */}
            {isLoadingAccesses && (
              <div className="flex items-center justify-center py-8">
                <Spinner />
              </div>
            )}

            {!isLoadingAccesses && filteredAccesses.length === 0 && (
              <div className="flex items-center justify-center py-8">
                <p className="text-muted-foreground">
                  {searchQuery || startDate || endDate || roleFilter !== 'all'
                    ? 'Nenhum acesso encontrado com os filtros aplicados'
                    : 'Nenhum acesso registrado'}
                </p>
              </div>
            )}

            {!isLoadingAccesses && filteredAccesses.length > 0 && (
              <>
                <SortableTable data={filteredAccesses}>
                  {(sortedData) => (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead sortable sortKey="user.name">
                            Usuário
                          </TableHead>
                          <TableHead sortable sortKey="user.email">
                            Email
                          </TableHead>
                          <TableHead sortable sortKey="user.role">
                            Função
                          </TableHead>
                          <TableHead sortable sortKey="ip">
                            IP
                          </TableHead>
                          <TableHead>Dispositivo</TableHead>
                          <TableHead sortable sortKey="createdAt">
                            Data/Hora
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedData.map((access) => (
                          <TableRow key={access.id}>
                            <TableCell>
                              <div className="font-medium text-foreground">
                                {access.user?.name || '-'}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm text-muted-foreground">
                                {access.user?.email || '-'}
                              </div>
                            </TableCell>
                            <TableCell>
                              {access.user?.role && (
                                <Badge
                                  className={
                                    access.user.role === 'ADMIN'
                                      ? 'bg-red-500/10 text-red-500 border-red-500/20'
                                      : access.user.role === 'GESTOR'
                                        ? 'bg-blue-500/10 text-blue-500 border-blue-500/20'
                                        : access.user.role === 'COLLABORATOR'
                                          ? 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20'
                                          : 'bg-green-500/10 text-green-500 border-green-500/20'
                                  }
                                >
                                  {access.user.role}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="text-sm text-muted-foreground">
                                {access.ip || '-'}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm text-muted-foreground max-w-xs truncate">
                                {access.userAgent || '-'}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm text-muted-foreground">
                                {formatDate(access.createdAt)}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </SortableTable>

                {/* Pagination */}
                {accessesData && accessesData.pagination.totalPages > 1 && (
                  <Pagination
                    currentPage={accessesData.pagination.page}
                    totalPages={accessesData.pagination.totalPages}
                    pageSize={accessesData.pagination.pageSize}
                    totalItems={accessesData.pagination.total}
                    onPageChange={setPage}
                  />
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
